// ds-color-ignore-file: dev-only / internal tooling surface
"use client"

import { useEffect, useRef, useState } from "react"
import { pickTextTarget } from "./pick-text-target"

// Grace window before the pencil hides once the cursor leaves the text box.
// Long enough to cross the small gap between the box and the corner pencil
// without the affordance vanishing mid-reach; short enough to feel responsive.
const HIDE_GRACE_MS = 160

// Top inset for the pencil so it never overlaps the fixed site navbar / banner
// (h-[72px] pill, plus a little breathing room).
const NAVBAR_INSET_PX = 80
// Pencil button size (square), used to keep it clamped inside the viewport.
const PENCIL_PX = 26

/**
 * Edit-mode hover layer: outlines the nearest text-bearing element under the
 * cursor and shows a pencil affordance. Clicking the pencil calls `onPick(el)`.
 * Pure next-intl (blue) in this MVP — CMS color-coding is a later phase.
 */
export function EditOverlay({
  active,
  canEdit,
  inlineEligible,
  onPick,
}: {
  active: boolean
  /** Only outline + offer the pencil when this returns true for the element. */
  canEdit: (el: HTMLElement) => boolean
  /** When true for the element, hovering signals in-place editing (I-beam +
   *  "Aa") instead of the panel-only pencil. */
  inlineEligible: (el: HTMLElement) => boolean
  onPick: (el: HTMLElement) => void
}) {
  const [target, setTarget] = useState<HTMLElement | null>(null)
  const [inline, setInline] = useState(false)
  // Bumped on scroll/resize so the pencil re-clamps to the region's visible
  // portion without needing a mouse move. (finding 3)
  const [, setReflow] = useState(0)
  // Mirror the live target in a ref so the move handler can compare/clean up
  // without the effect re-subscribing on every hover (listeners attach once).
  const targetRef = useRef<HTMLElement | null>(null)
  const hideTimer = useRef<number | null>(null)

  // While a target is held, keep the pencil pinned to the region's visible
  // portion as the page scrolls (a tall region's top scrolls off / under the
  // navbar). Passive listeners; only mounted when there's a target to track.
  useEffect(() => {
    if (!active || !target) return
    function onReflow() {
      setReflow((n) => n + 1)
    }
    window.addEventListener("scroll", onReflow, {
      passive: true,
      capture: true,
    })
    window.addEventListener("resize", onReflow, { passive: true })
    return () => {
      window.removeEventListener("scroll", onReflow, true)
      window.removeEventListener("resize", onReflow)
    }
  }, [active, target])

  useEffect(() => {
    function setTargetEl(el: HTMLElement | null) {
      targetRef.current = el
      setTarget(el)
    }
    function cancelHide() {
      if (hideTimer.current !== null) {
        clearTimeout(hideTimer.current)
        hideTimer.current = null
      }
    }
    function scheduleHide() {
      if (hideTimer.current !== null) return
      hideTimer.current = window.setTimeout(() => {
        hideTimer.current = null
        targetRef.current?.style.removeProperty("outline")
        targetRef.current?.style.removeProperty("cursor")
        setTargetEl(null)
      }, HIDE_GRACE_MS)
    }
    if (!active) {
      cancelHide()
      targetRef.current?.style.removeProperty("outline")
      targetRef.current?.style.removeProperty("cursor")
      setTargetEl(null)
      return
    }
    function onMove(e: MouseEvent) {
      const hit = document.elementFromPoint(
        e.clientX,
        e.clientY
      ) as HTMLElement | null
      // Over the pencil or cockpit chrome — this is the hover-bridge: reaching
      // the corner pencil crosses editor UI, not page text, so keep the current
      // target alive instead of treating it as "left the element".
      if (!hit || hit.closest("[data-ahead-editor-ui]")) {
        cancelHide()
        return
      }
      const el = pickTextTarget(hit)
      // Nothing editable here. Don't yank the pencil the instant the cursor
      // grazes a gap — schedule a hide on a grace timer so it stays reachable;
      // re-entering text (below) cancels it.
      if (!el || !canEdit(el)) {
        scheduleHide()
        return
      }
      cancelHide()
      if (el === targetRef.current) return
      targetRef.current?.style.removeProperty("outline")
      targetRef.current?.style.removeProperty("cursor")
      const eligible = inlineEligible(el)
      el.style.outline = eligible ? "2px solid #2b6cff" : "2px dashed #9aa0aa"
      el.style.outlineOffset = "2px"
      el.style.cursor = eligible ? "text" : "pointer"
      setInline(eligible)
      setTargetEl(el)
    }
    function onDblClick(e: MouseEvent) {
      const t = targetRef.current
      if (!t) return
      const el = document.elementFromPoint(e.clientX, e.clientY)
      if (!el || el.closest("[data-ahead-editor-ui]")) return
      e.preventDefault()
      window.getSelection()?.removeAllRanges()
      onPick(t)
    }
    document.addEventListener("mousemove", onMove, true)
    document.addEventListener("dblclick", onDblClick, true)
    return () => {
      cancelHide()
      document.removeEventListener("mousemove", onMove, true)
      document.removeEventListener("dblclick", onDblClick, true)
    }
  }, [active, onPick, canEdit, inlineEligible])

  if (!active || !target) return null
  const r = target.getBoundingClientRect()
  // Horizontal: top-right of the region, kept inside the viewport.
  const left = Math.min(window.innerWidth - (PENCIL_PX + 6), r.right - 10)
  // Vertical: stay reachable while any part of the region is in view. Clamp the
  // pencil to the region's visible span so on a tall region whose top has
  // scrolled off it rides the viewport instead of vanishing — and never let it
  // sit under the fixed navbar (NAVBAR_INSET_PX) or past the region's bottom.
  const regionTop = r.top - 10
  const regionBottom = r.bottom - PENCIL_PX
  const top = Math.max(
    NAVBAR_INSET_PX,
    Math.min(regionTop, window.innerHeight - (PENCIL_PX + 6), regionBottom)
  )
  return (
    <button
      data-ahead-editor-ui
      aria-label="Edit text"
      onClick={() => onPick(target)}
      style={{
        position: "fixed",
        left,
        top,
        zIndex: 2147483601,
        width: PENCIL_PX,
        height: PENCIL_PX,
        borderRadius: 7,
        border: "none",
        background: inline ? "#2b6cff" : "#6b7280",
        color: "#fff",
        cursor: "pointer",
      }}
    >
      {inline ? "Aa" : "✎"}
    </button>
  )
}
